import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HTMLAttributes } from 'react'
import {
  Card,
  ErrorAlert,
  formatDate,
  formatTime,
  LoadingScreen
} from '@repo/ui'
import { CalendarIcon20, ChatIcon32, ScheduleIcon20 } from '@repo/ui/icons'
import { BfnStoreDataEntry } from '@/store/useBfnStore'
import { useBefehlsnachrichtHashVerification } from '@/features/befehlsnachrichten/hooks/useBefehlsnachrichtHashVerification'
import { checkVisuellerStatusMatchesStatus } from '@/features/befehlsnachrichten/utils/befehlsnachricht-status'
import BfnStatusTag from './BfnStatusTag'

export interface BfnListItemProps extends HTMLAttributes<HTMLDivElement> {
  id: string
  data: BfnStoreDataEntry
  'data-testid': string
}

export default function BfnListItem({
  id,
  data,
  'data-testid': testId
}: Readonly<BfnListItemProps>) {
  const navigate = useNavigate()
  const { t } = useTranslation('bfn')
  const { isHashValid } = useBefehlsnachrichtHashVerification(
    data.befehlsnachricht
  )

  const openBefehlsnachricht = () => {
    navigate(`/befehlsnachrichten/${id}`)
  }

  const bfn = data.befehlsnachricht

  if (isHashValid === null) {
    return <LoadingScreen data-testid={`${testId}-loading`} />
  }

  if (!isHashValid) {
    return (
      <ErrorAlert data-testid={`${testId}-error`}>
        {t('item.errors.invalid_hash', { id: bfn.id })}
      </ErrorAlert>
    )
  }

  if (
    checkVisuellerStatusMatchesStatus(
      data.befehlsnachricht.visuellerStatus,
      data.befehlsnachricht.status
    ).error
  ) {
    return (
      <ErrorAlert data-testid={`${testId}-error`}>
        {t('item.errors.visueller_status_mismatch', {
          visuellerStatus: bfn.visuellerStatus,
          status: bfn.status
        })}
      </ErrorAlert>
    )
  }

  return (
    <button type="button" onClick={openBefehlsnachricht} data-testid={testId}>
      <Card className="flex flex-row px-1 py-0.5">
        <div className="flex flex-row flex-grow no-underline">
          <div className="flex flex-col flex-grow items-start">
            <BfnStatusTag
              status={data.befehlsnachricht.status}
              visuellerStatus={data.befehlsnachricht.visuellerStatus}
              manuellWiderrufen={!!data.manuellWiderrufenAt}
              data-testid={`${testId}-status`}
            />
            <div className="flex flex-grow">
              {/* Icon */}
              <div className="flex items-center">
                <ChatIcon32 data-testid={`${testId}-icon`} />
              </div>
              {/* Content */}
              <div className="flex flex-col flex-grow items-start pl-1">
                <div className="text-cool-gray-700 dark:text-cool-gray-200 text-sm">
                  <span>
                    <CalendarIcon20 data-testid={`${testId}-date-icon`} />
                    <span
                      className="pl-0.5"
                      data-testid={`${testId}-date-text`}
                    >
                      {formatDate(bfn.versandzeitpunkt)}
                    </span>
                  </span>
                  <span className="pl-1">
                    <ScheduleIcon20 data-testid={`${testId}-time-icon`} />
                    <span
                      className="pl-0.5"
                      data-testid={`${testId}-time-text`}
                    >
                      {formatTime(bfn.versandzeitpunkt)}
                    </span>
                  </span>
                </div>
                <span
                  className="font-bold"
                  data-testid={`${testId}-eindeutigeKennung`}
                >
                  {bfn.eindeutigeKennung}
                </span>
                <span data-testid={`${testId}-zugnummer`}>
                  {t('item.zugnummer', { value: bfn.zugnummer })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </button>
  )
}
